// src/utils/championshipPage.js
// ============================================================================
// The championship page's data, rebuilt from the public tournaments/{id} doc
// with the same fields and rules as getChampionshipPage
// (functions/modules/brandedEvents.js). The page loads once through the
// callable (the slug lookup is server-only), then listens to the event doc,
// so a sponsor approved later shows without a reload, the way Event Detail
// does (Dennis 11-03).
// ============================================================================

// Sponsor sign-ups close when the event goes live (brandedEvents.js has the same set).
export const SIGNUP_CLOSED_STATUSES = new Set(['live', 'in_progress', 'completed', 'finished', 'cancelled', 'canceled']);

function toMs(val) {
  if (val == null) return null;
  if (typeof val.toMillis === 'function') return val.toMillis();
  if (typeof val.seconds === 'number') return val.seconds * 1000;
  if (typeof val._seconds === 'number') return val._seconds * 1000;
  if (typeof val === 'number' || typeof val === 'string') return val;
  return null;
}

/** Page data from an event doc, or null when the doc no longer has this page. */
export function pageDataFromEvent(tournamentId, ev, slug) {
  if (!ev || !ev.championship || ev.championship.slug !== slug) return null;
  const branded = ev.branded === true;
  const status = ev.status || 'scheduled';
  const c = ev.championship;
  return {
    tournamentId,
    name: ev.name || ev.title || '',
    game: ev.game || null,
    startAt: toMs(ev.startAt),
    status,
    maxEntrants: Number(ev.maxEntrants ?? ev.bracketSize ?? 0) || null,
    ...(ev.format ? { format: String(ev.format) } : {}),
    branded,
    organizer: ev.organizer ? { name: ev.organizer.name, url: ev.organizer.url || null } : null,
    publisherDisclaimer: ev.publisherDisclaimer || null,
    championship: {
      slug: c.slug,
      venue: { name: c.venue?.name || '', city: c.venue?.city || '' },
      stages: Array.isArray(c.stages)
        ? c.stages.map((st) => ({ name: st.name || '', when: st.when || '', where: st.where || '' }))
        : [],
      rulesText: c.rulesText || '',
    },
    sponsorSignupOpen: branded && ev.sponsorSignupOpen === true
      && !SIGNUP_CLOSED_STATUSES.has(String(status).toLowerCase()),
    sponsors: (branded && Array.isArray(ev.sponsors) ? ev.sponsors : []).map((s) => ({
      sponsorshipId: s.sponsorshipId || null,
      level: s.level,
      name: s.name,
      logoAssetId: s.logoAssetId || null,
      url: s.url || null,
      prizeNote: s.prizeNote || null,
    })),
  };
}

/**
 * Listens to the event doc. onData gets fresh page data on every change;
 * onGone runs if the doc is deleted or no longer carries this page. A read
 * error keeps the last data on screen. Returns the unsubscribe function.
 * @param {{ db, doc, onSnapshot }} fs the Firestore handles (passed in so tests can stub them)
 */
export function subscribeChampionshipEvent(fs, tournamentId, slug, onData, onGone) {
  return fs.onSnapshot(
    fs.doc(fs.db, 'tournaments', tournamentId),
    (snap) => {
      const data = snap && snap.exists() ? pageDataFromEvent(snap.id, snap.data(), slug) : null;
      if (data) onData(data); else onGone();
    },
    () => {},
  );
}
